<template>
  <div
    class="service-visits-list"
    :class="{ 'service-visits-list--fullscreen p-4': fullscreen }"
  >
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title service-visits-list__title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SERVICE.VISITS") }}
            <i class="mr-2"></i>
            <small>{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar service-visits-list__toolbar">
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-board-service-visits'
                : 'board-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-list__toolbar-button"
          >
            <i class="flaticon2-layers-1"></i>
            {{ $t("SERVICE_VISITS.BOARD") }}
          </router-link>
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-calendar-service-visits'
                : 'calendar-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-list__toolbar-button"
          >
            <i class="flaticon-calendar"></i>
            {{ $t("SERVICE_VISITS.CALENDAR") }}
          </router-link>
          <router-link
            v-if="!fullscreen && checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{ name: 'fullscreen-list-service-visits' }"
            target="_blank"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-list__toolbar-button service-visits-list__fullscreen-link"
          >
            <i class="flaticon2-expand"></i>
            {{ $t("BUTTON.FULLSCREEN") }}
          </router-link>
          <router-link
            v-permission="['service-visits-create']"
            :to="{ name: 'add-service-visits' }"
            class="btn btn-light-success font-weight-bolder ml-2 service-visits-list__toolbar-button"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </router-link>
        </div>
      </template>

      <v-card v-show="!isPageLoading">
        <ServiceVisitFilters
          :filters.sync="filters"
          :search-query.sync="searchQuery"
          :mechanics="mechanics"
          :priorities="priorities"
          :service-locations="serviceLocations"
          :statuses="filterStatuses"
          :show-mechanic="canShowServiceVisitMechanicFilter"
          mobile-sheet
          @search="doSearch"
          @clear="doClear"
        />
        <ResponsiveDataTable
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isListLoading"
          :error="hasListError"
          :error-text="$t('SERVICE_VISITS.LIST_LOAD_ERROR')"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          class="elevation-1"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
          @update:options="getDataFromApi"
        >
          <template v-slot:[`item.machine`]="{ item }">
            {{ machineName(item.machine) }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <span class="service-visits-list__status d-inline-flex align-items-center">
              <span class="service-visits-list__status-dot rounded-circle mr-2" :style="statusDotStyle(item.status)"></span>
              {{ item.status ? item.status.name : "-" }}
            </span>
          </template>
          <template v-slot:[`item.priority`]="{ item }">
            {{ item.priority ? item.priority.name : "" }}
          </template>
          <template v-slot:[`item.dateFrom`]="{ item }">
            {{ formatShortDate(item.dateFrom) }}
          </template>
          <template v-slot:[`item.dateTo`]="{ item }">
            {{ formatShortDate(item.dateTo) }}
          </template>
          <template v-slot:[`item.location`]="{ item }">
            <span>{{ locationText(item) }}</span>
          </template>
          <template v-slot:[`item.duration`]="{ item }">
            <span>{{ item.duration !== null && item.duration !== undefined ? item.duration : "-" }}</span>
          </template>
          <template v-slot:[`item.serviceMileage`]="{ item }">
            {{ serviceMileageText(item) }}
          </template>
          <template v-slot:[`item.mechanics`]="{ item }">
            {{ mechanicNames(item.mechanics) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
              :to="{ name: 'show-service-visits', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.VIEW") }}</span>
              </v-tooltip>
            </router-link>
            <router-link
              v-if="canEditVisit(item)"
              :to="{ name: 'edit-service-visits', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
          </template>
          <template v-slot:mobile-summary-status="{ item }">
            <span class="service-visits-list__status d-inline-flex align-items-center">
              <span class="service-visits-list__status-dot rounded-circle mr-2" :style="statusDotStyle(item.status)"></span>
              {{ item.status ? item.status.name : "-" }}
            </span>
          </template>
          <template v-slot:mobile-summary-actions="{ item }">
            <v-btn
              v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
              :to="{ name: 'show-service-visits', params: { id: item.id } }"
              :aria-label="$t('LABELS.VIEW')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="canEditVisit(item)"
              :to="{ name: 'edit-service-visits', params: { id: item.id } }"
              :aria-label="$t('LABELS.EDIT')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      />
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { FETCH_SERVICE_VISITS } from "@/modules/service-visits/store/service-visits.module";
import {
  isServiceVisitFinished,
  serviceVisitListParams,
  serviceVisitLocationText
} from "../services/service-visit.mapper";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import { serviceVisitFilterOptionsMixin } from "@/modules/service-visits/mixins/serviceVisitFilterOptionsMixin.js";
import ServiceVisitFilters from "@/modules/service-visits/components/ServiceVisitFilters.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";

export default {
  mixins: [permissionMixin, serviceVisitFilterOptionsMixin],
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ServiceVisitFilters,
    ResponsiveDataTable
  },
  data() {
    return {
      isPageLoading: true,
      hasListError: false,
      isListLoading: false,
      listRequestId: 0,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        sortBy: ["dateFrom"],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      headers: [
        {
          text: this.$i18n.t("FORM_LABELS.MACHINE"),
          value: "machine",
          sortable: false
        },
        {
          text: this.$i18n.t("FORM_LABELS.STATUS"),
          value: "status",
          sortable: false
        },
        {
          text: this.$i18n.t("FORM_LABELS.PRIORITY"),
          value: "priority",
          sortable: false
        },
        { text: this.$i18n.t("FORM_LABELS.DATE_FROM"), value: "dateFrom" },
        { text: this.$i18n.t("FORM_LABELS.DATE_TO"), value: "dateTo" },
        { text: this.$i18n.t("FORM_LABELS.DURATION"), value: "duration" },
        {
          text: this.$i18n.t("SERVICE_VISITS.COMPLETION_MILEAGE_TITLE"),
          value: "serviceMileage",
          sortable: false
        },
        {
          text: this.$i18n.t("FORM_LABELS.LOCATION"),
          value: "location",
          sortable: false
        },
        {
          text: this.$i18n.t("MENU.ITEM.SERVICE.MECHANICS"),
          value: "mechanics",
          sortable: false
        },
        {
          align: "right",
          text: this.$i18n.t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "12%"
        }
      ]
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.VISITS"),
        route: { name: "list-service-visits" }
      },
      { title: this.$i18n.t("MENU.ITEM.ASSETS.LIST") }
    ]);
    this.loadServiceVisitFilterOptions();
  },
  computed: {
    params() {
      return {
        ...this.options,
        ...this.activeServiceVisitFilters,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.headers;
    },
    mobileSummaryFields() {
      return [
        {
          key: "machine",
          label: this.$t("FORM_LABELS.MACHINE"),
          primary: true,
          value: item => this.machineName(item.machine) || "-"
        },
        {
          key: "status",
          label: this.$t("FORM_LABELS.STATUS"),
          slot: "status"
        },
        {
          key: "dates",
          label: this.$t("FORM_LABELS.DATE_FROM"),
          value: item => this.visitDateRange(item)
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "priority",
          label: this.$t("FORM_LABELS.PRIORITY"),
          value: item => item.priority ? item.priority.name : "-"
        },
        {
          key: "location",
          label: this.$t("FORM_LABELS.LOCATION"),
          value: item => this.locationText(item) || "-"
        },
        {
          key: "mechanics",
          label: this.$t("MENU.ITEM.SERVICE.MECHANICS"),
          value: item => this.mechanicNames(item.mechanics) || "-"
        },
        {
          key: "duration",
          label: this.$t("FORM_LABELS.DURATION"),
          value: item => item.duration !== null && item.duration !== undefined ? item.duration : "-"
        },
        {
          key: "serviceMileage",
          label: this.$t("SERVICE_VISITS.COMPLETION_MILEAGE_TITLE"),
          value: item => this.serviceMileageText(item)
        }
      ];
    }
  },
  methods: {
    doSearch() {
      if (this.options.page === 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      this.resetServiceVisitFilters();
      if (this.options.page === 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    getDataFromApi() {
      const requestId = ++this.listRequestId;
      const params = serviceVisitListParams(this.params);

      this.hasListError = false;
      this.isListLoading = true;
      params.length = params.itemsPerPage;
      params.start =
        params.page === 1 ? 0 : params.itemsPerPage * (params.page - 1);

      return this.$store
        .dispatch(FETCH_SERVICE_VISITS, this.$url.transformParams(params))
        .then(data => {
          if (requestId !== this.listRequestId) {
            return;
          }

          this.items = data.data.items;
          this.totalItems = data.data.totalItemsCount;
        })
        .catch(() => {
          if (requestId !== this.listRequestId) {
            return;
          }

          this.hasListError = true;
        })
        .finally(() => {
          if (requestId !== this.listRequestId) {
            return;
          }

          this.isPageLoading = false;
          this.isListLoading = false;
        });
    },
    machineName(machine) {
      return machine ? machine.serviceDisplayName || machine.name || "" : "";
    },
    mechanicNames(mechanics) {
      return (mechanics || [])
        .map(mechanic => `${mechanic.firstName || ""} ${mechanic.lastName || ""}`.trim())
        .join(", ");
    },
    locationText(item) {
      return serviceVisitLocationText(item, this.$t("FORM_LABELS.IS_ON_ROAD"));
    },
    formatShortDate(value) {
      if (!value) {
        return "-";
      }

      const [, month, day] = String(value).split("-");

      return month && day ? `${day}/${month}` : value;
    },
    visitDateRange(item) {
      const dateFrom = this.formatShortDate(item.dateFrom);
      const dateTo = this.formatShortDate(item.dateTo);

      return dateFrom === dateTo || dateTo === "-" ? dateFrom : `${dateFrom} – ${dateTo}`;
    },
    serviceMileageText(item) {
      if (
        !item.status ||
        item.status.slug !== "finished" ||
        item.serviceMileage === null ||
        item.serviceMileage === undefined
      ) {
        return "-";
      }

      return `${item.serviceMileage} ${item.machine && item.machine.isMileage ? this.$t("LABELS.KM") : this.$t("SERVICE_VISITS.MOTO_HOURS_SHORT")}`;
    },
    statusDotStyle(status) {
      return { backgroundColor: status && status.color ? status.color : "#3699ff" };
    },
    canEditVisit(item) {
      return (
        !isServiceVisitFinished(item) &&
        this.checkIfUserHasPermission(["service-visits-update"])
      );
    }
  }
};
</script>

<style scoped>
.service-visits-list--fullscreen {
  min-width: calc(100vw - 2rem);
  width: max-content;
}

.service-visits-list--fullscreen ::v-deep .card,
.service-visits-list--fullscreen ::v-deep .v-card {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

.service-visits-list--fullscreen ::v-deep .card-body {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

.service-visits-list--fullscreen ::v-deep .v-data-table {
  min-width: calc(100vw - 5rem);
  overflow: visible;
  width: max-content;
}

.service-visits-list--fullscreen ::v-deep .v-data-table__wrapper {
  overflow: visible !important;
  width: max-content;
}

.service-visits-list--fullscreen ::v-deep .v-data-table table {
  min-width: 1440px;
}

.service-visits-list__status {
  color: #3f4254;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
}

.service-visits-list__status-dot {
  height: 8px;
  min-height: 8px;
  min-width: 8px;
  width: 8px;
}

@media (max-width: 767.98px) {
  .service-visits-list ::v-deep .card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .service-visits-list__title {
    flex: 1 1 100%;
    margin-bottom: 0.75rem;
  }

  .service-visits-list__toolbar {
    display: grid;
    flex: 1 1 100%;
    gap: 0.5rem;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
  }

  .service-visits-list__toolbar-button {
    align-items: center;
    display: flex;
    font-size: 0.75rem;
    justify-content: center;
    margin-left: 0 !important;
    min-height: 2.5rem;
    padding: 0.5rem;
    white-space: nowrap;
  }

  .service-visits-list__toolbar-button i {
    margin-right: 0.25rem;
  }

  .service-visits-list__fullscreen-link {
    display: none;
  }
}
</style>
